import React, { useState, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRightIcon, ChevronLeftIcon, SaveIcon, CheckIcon, InfoIcon, TargetIcon, GiftIcon, WalletIcon, ShieldCheckIcon, PlusIcon, TrashIcon, AlertCircleIcon } from 'lucide-react';
import Card from '../../components/ui/Card';
const STEPS = [{
  id: 1,
  name: 'Master Config',
  icon: InfoIcon
}, {
  id: 2,
  name: 'Target Rules',
  icon: TargetIcon
}, {
  id: 3,
  name: 'Reward Structure',
  icon: GiftIcon
}, {
  id: 4,
  name: 'Budget & Control',
  icon: WalletIcon
}, {
  id: 5,
  name: 'Review',
  icon: ShieldCheckIcon
}];
interface Slab {
  id: number;
  minTxnCount: number;
  maxTxnCount: number;
  slabRewardType: 'percentage' | 'amount';
  slabRewardValue: number;
}
// Format number with 1,000 separators
const formatAmount = (value: number): string => {
  if (!value && value !== 0) return '';
  if (value === 0) return '';
  return value.toLocaleString('en-PK');
};
// Parse formatted string back to number
const parseAmount = (value: string): number => {
  const cleaned = value.replace(/,/g, '');
  const num = Number(cleaned);
  return isNaN(num) ? 0 : num;
};
const CashbackCampaignForm: React.FC = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    // Step 1: Master Config
    name: '',
    code: 'CB-' + Math.floor(Math.random() * 10000),
    description: '',
    startDate: '',
    endDate: '',
    qualificationRule: 'once_per_cycle',
    // Step 2: Target Rules (Frequency & Stacking moved here)
    frequency: 'monthly',
    stacking: false,
    targetLogic: 'transaction_count',
    targetValue: 0,
    minTxnAmount: 0,
    eligibleTypes: ['raast_qr', 'card'],
    merchantFilter: 'all',
    // Step 3: Reward Structure
    rewardType: 'fixed',
    rewardValue: 0,
    maxRewardPerCycle: 0,
    maxRewardPerCampaign: 0,
    // Step 4: Budget
    totalBudget: 0,
    cycleBudget: 0,
    autoStop: true
  });
  const [slabs, setSlabs] = useState<Slab[]>([{
    id: 1,
    minTxnCount: 0,
    maxTxnCount: 0,
    slabRewardType: 'amount',
    slabRewardValue: 0
  }]);
  const [slabError, setSlabError] = useState('');
  // Amount input display values (formatted strings)
  const [amountDisplays, setAmountDisplays] = useState<Record<string, string>>({});
  const handleAmountChange = (field: string, rawValue: string) => {
    const digitsOnly = rawValue.replace(/[^0-9]/g, '');
    const numValue = Number(digitsOnly);
    setAmountDisplays({
      ...amountDisplays,
      [field]: digitsOnly ? Number(digitsOnly).toLocaleString('en-PK') : ''
    });
    setFormData({
      ...formData,
      [field]: numValue
    });
  };
  const getAmountDisplay = (field: string, value: number): string => {
    if (amountDisplays[field] !== undefined) return amountDisplays[field];
    return value ? formatAmount(value) : '';
  };
  // Slab overlap validation
  const validateSlabs = (currentSlabs: Slab[]): string => {
    for (let i = 0; i < currentSlabs.length; i++) {
      const slab = currentSlabs[i];
      if (slab.minTxnCount >= slab.maxTxnCount && slab.maxTxnCount !== 0) {
        return `Slab ${i + 1}: Min Txn Count must be less than Max Txn Count`;
      }
      if (slab.slabRewardType === 'percentage' && slab.slabRewardValue > 10) {
        return `Slab ${i + 1}: Percentage cannot exceed 10%`;
      }
      for (let j = i + 1; j < currentSlabs.length; j++) {
        const other = currentSlabs[j];
        if (slab.minTxnCount < other.maxTxnCount && slab.maxTxnCount > other.minTxnCount) {
          return `Slab ${i + 1} and Slab ${j + 1} have overlapping ranges`;
        }
      }
    }
    return '';
  };
  const addSlab = () => {
    const lastSlab = slabs[slabs.length - 1];
    const newSlab: Slab = {
      id: Date.now(),
      minTxnCount: lastSlab ? lastSlab.maxTxnCount : 0,
      maxTxnCount: 0,
      slabRewardType: 'amount',
      slabRewardValue: 0
    };
    const updated = [...slabs, newSlab];
    setSlabs(updated);
    setSlabError(validateSlabs(updated));
  };
  const removeSlab = (id: number) => {
    if (slabs.length <= 1) return;
    const updated = slabs.filter(s => s.id !== id);
    setSlabs(updated);
    setSlabError(validateSlabs(updated));
  };
  const updateSlab = (id: number, field: keyof Slab, value: any) => {
    const updated = slabs.map(s => {
      if (s.id === id) {
        const newSlab = {
          ...s,
          [field]: value
        };
        if (field === 'slabRewardType' && value === 'percentage' && newSlab.slabRewardValue > 10) {
          newSlab.slabRewardValue = 10;
        }
        return newSlab;
      }
      return s;
    });
    setSlabs(updated);
    setSlabError(validateSlabs(updated));
  };
  const handleNext = () => {
    if (currentStep < STEPS.length) setCurrentStep(prev => prev + 1);
  };
  const handleBack = () => {
    if (currentStep > 1) setCurrentStep(prev => prev - 1);
  };
  const handleSubmit = () => {
    navigate('/cashback/campaigns');
  };
  const renderStep1 = () => <div className="space-y-6" data-id="element-3180">
      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700" data-id="element-3181">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4" data-id="element-3182">
          Campaign Master Configuration
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-3183">
          <div data-id="element-3184">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-3185">
              Campaign Name
            </label>
            <input type="text" className="w-full border border-gray-300 dark:border-gray-600 rounded-md p-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400" value={formData.name} onChange={e => setFormData({
            ...formData,
            name: e.target.value
          })} placeholder="e.g. Q4 Merchant Activation" data-id="element-3186" />
          </div>
          <div data-id="element-3187">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-3188">
              Campaign Code
            </label>
            <input type="text" className="w-full border border-gray-300 dark:border-gray-600 rounded-md p-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-400" value={formData.code} readOnly data-id="element-3189" />
          </div>
          <div className="md:col-span-2" data-id="element-3190">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-3191">
              Description
            </label>
            <textarea className="w-full border border-gray-300 dark:border-gray-600 rounded-md p-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400" rows={3} value={formData.description} onChange={e => setFormData({
            ...formData,
            description: e.target.value
          })} data-id="element-3192" />
          </div>
          <div data-id="element-3193">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-3194">
              Start Date
            </label>
            <input type="datetime-local" className="w-full border border-gray-300 dark:border-gray-600 rounded-md p-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-white" value={formData.startDate} onChange={e => setFormData({
            ...formData,
            startDate: e.target.value
          })} data-id="element-3195" />
          </div>
          <div data-id="element-3196">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-3197">
              End Date
            </label>
            <input type="datetime-local" className="w-full border border-gray-300 dark:border-gray-600 rounded-md p-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-white" value={formData.endDate} onChange={e => setFormData({
            ...formData,
            endDate: e.target.value
          })} data-id="element-3198" />
          </div>
        </div>
      </div>
    </div>;
  const renderStep2 = () => <div className="space-y-6" data-id="element-3199">
      {/* Frequency & Stacking Rule — moved here from Master Config */}
      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700" data-id="element-3200">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4" data-id="element-3201">
          Frequency & Stacking
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-3202">
          <div data-id="element-3203">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-3204">
              Frequency
            </label>
            <select className="w-full border border-gray-300 dark:border-gray-600 rounded-md p-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-white" value={formData.frequency} onChange={e => setFormData({
            ...formData,
            frequency: e.target.value
          })} data-id="element-3205">
              <option value="daily" data-id="element-3206">Daily</option>
              <option value="weekly" data-id="element-3207">Weekly</option>
              <option value="monthly" data-id="element-3208">Monthly</option>
              <option value="quarterly" data-id="element-3209">Quarterly</option>
              <option value="annually" data-id="element-3210">Annually</option>
            </select>
          </div>
          <div data-id="element-3211">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-3212">
              Stacking Rule
            </label>
            <div className="flex items-center space-x-4 mt-2 text-gray-700 dark:text-gray-300" data-id="element-3213">
              <label className="flex items-center" data-id="element-3214">
                <input type="radio" checked={!formData.stacking} onChange={() => setFormData({
                ...formData,
                stacking: false
              })} className="mr-2" data-id="element-3215" />
                No Stacking
              </label>
              <label className="flex items-center" data-id="element-3216">
                <input type="radio" checked={formData.stacking} onChange={() => setFormData({
                ...formData,
                stacking: true
              })} className="mr-2" data-id="element-3217" />
                Allow Stacking
              </label>
            </div>
          </div>
        </div>
      </div>

      {/* Target Definition */}
      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700" data-id="element-3218">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4" data-id="element-3219">
          Target Definition Engine
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-3220">
          <div data-id="element-3221">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-3222">
              Target Logic Type
            </label>
            <select className="w-full border border-gray-300 dark:border-gray-600 rounded-md p-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-white" value={formData.targetLogic} onChange={e => setFormData({
            ...formData,
            targetLogic: e.target.value
          })} data-id="element-3223">
              <option value="transaction_count" data-id="element-3224">Transaction Count Based</option>
              <option value="transaction_value" data-id="element-3225">Transaction Value Based</option>
              <option value="first_x" data-id="element-3226">First X Transactions (Activation)</option>
            </select>
          </div>
          <div data-id="element-3227">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-3228">
              Target Threshold
            </label>
            {formData.targetLogic === 'transaction_value' ? <div className="relative" data-id="element-3229">
                <input type="text" className="w-full border border-gray-300 dark:border-gray-600 rounded-md p-2 pr-12 bg-white dark:bg-gray-800 text-gray-900 dark:text-white" placeholder="0" value={getAmountDisplay('targetValue', formData.targetValue)} onChange={e => handleAmountChange('targetValue', e.target.value)} data-id="element-3230" />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" data-id="element-3231">
                  PKR
                </span>
              </div> : <input type="number" className="w-full border border-gray-300 dark:border-gray-600 rounded-md p-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-white" placeholder="Count" value={formData.targetValue || ''} onChange={e => setFormData({
            ...formData,
            targetValue: Number(e.target.value)
          })} data-id="element-3232" />}
          </div>
          <div data-id="element-3233">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-3234">
              Min Transaction Amount
            </label>
            <div className="relative" data-id="element-3235">
              <input type="text" className="w-full border border-gray-300 dark:border-gray-600 rounded-md p-2 pr-12 bg-white dark:bg-gray-800 text-gray-900 dark:text-white" placeholder="0" value={getAmountDisplay('minTxnAmount', formData.minTxnAmount)} onChange={e => handleAmountChange('minTxnAmount', e.target.value)} data-id="element-3236" />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" data-id="element-3237">
                PKR
              </span>
            </div>
          </div>
          <div data-id="element-3238">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-3239">
              Eligible Transaction Types
            </label>
            <div className="flex flex-wrap gap-3 mt-2 text-gray-700 dark:text-gray-300" data-id="element-3240">
              {['RAAST QR', 'Card', 'Online'].map(type => <label key={type} className="flex items-center" data-id="element-3241">
                  <input type="checkbox" className="mr-2" defaultChecked data-id="element-3242" />
                  {type}
                </label>)}
            </div>
          </div>
        </div>
      </div>
    </div>;
  const renderStep3 = () => <div className="space-y-6" data-id="element-3243">
      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700" data-id="element-3244">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4" data-id="element-3245">
          Reward Structure
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-3246">
          <div data-id="element-3247">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-3248">
              Reward Type
            </label>
            <select className="w-full border border-gray-300 dark:border-gray-600 rounded-md p-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-white" value={formData.rewardType} onChange={e => {
            const newType = e.target.value;
            let newValue = formData.rewardValue;
            if (newType === 'percentage' && newValue > 10) newValue = 10;
            setFormData({
              ...formData,
              rewardType: newType,
              rewardValue: newValue
            });
          }} data-id="element-3249">
              <option value="fixed" data-id="element-3250">Fixed Amount</option>
              <option value="percentage" data-id="element-3251">Percentage of Volume</option>
              <option value="tiered" data-id="element-3252">Tiered Slab-Based</option>
            </select>
          </div>

          {/* Fixed Amount input */}
          {formData.rewardType === 'fixed' && <div data-id="element-3253">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-3254">
                Amount (PKR)
              </label>
              <div className="relative" data-id="element-3255">
                <input type="text" className="w-full border border-gray-300 dark:border-gray-600 rounded-md p-2 pr-12 bg-white dark:bg-gray-800 text-gray-900 dark:text-white" placeholder="0" value={getAmountDisplay('rewardValue', formData.rewardValue)} onChange={e => handleAmountChange('rewardValue', e.target.value)} data-id="element-3256" />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" data-id="element-3257">
                  PKR
                </span>
              </div>
            </div>}

          {/* Percentage input with max 10 validation */}
          {formData.rewardType === 'percentage' && <div data-id="element-3258">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-3259">
                Percentage (%)
              </label>
              <div className="relative" data-id="element-3260">
                <input type="number" className="w-full border border-gray-300 dark:border-gray-600 rounded-md p-2 pr-10 bg-white dark:bg-gray-800 text-gray-900 dark:text-white" placeholder="0" min={0} max={10} step={0.1} value={formData.rewardValue || ''} onChange={e => {
              let val = Number(e.target.value);
              if (val > 10) val = 10;
              if (val < 0) val = 0;
              setFormData({
                ...formData,
                rewardValue: val
              });
            }} data-id="element-3261" />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" data-id="element-3262">
                  %
                </span>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1" data-id="element-3263">
                Maximum allowed: 10%
              </p>
            </div>}
        </div>

        {/* Tiered Slab-Based Section */}
        {formData.rewardType === 'tiered' && <div className="mt-6" data-id="element-3264">
            <div className="flex items-center justify-between mb-4" data-id="element-3265">
              <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300" data-id="element-3266">
                Reward Slabs
              </h4>
              <button onClick={addSlab} className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 rounded-md hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors" data-id="element-3267">
                <PlusIcon className="h-3.5 w-3.5 mr-1" data-id="element-3268" />
                Add Slab
              </button>
            </div>

            {slabError && <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md flex items-start" data-id="element-3269">
                <AlertCircleIcon className="h-4 w-4 text-red-500 dark:text-red-400 mt-0.5 mr-2 flex-shrink-0" data-id="element-3270" />
                <p className="text-xs text-red-700 dark:text-red-300" data-id="element-3271">
                  {slabError}
                </p>
              </div>}

            <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden" data-id="element-3272">
              {/* Table Header */}
              <div className="grid grid-cols-12 gap-0 bg-gray-50 dark:bg-gray-700 px-4 py-2.5 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-3273">
                <div className="col-span-1 text-center" data-id="element-3274">#</div>
                <div className="col-span-2" data-id="element-3275">Min Txn Count</div>
                <div className="col-span-2" data-id="element-3276">Max Txn Count</div>
                <div className="col-span-3" data-id="element-3277">Reward Type</div>
                <div className="col-span-3" data-id="element-3278">Reward Value</div>
                <div className="col-span-1 text-center" data-id="element-3279">Action</div>
              </div>

              {/* Slab Rows */}
              {slabs.map((slab, index) => <div key={slab.id} className="grid grid-cols-12 gap-0 px-4 py-3 border-t border-gray-200 dark:border-gray-700 items-center" data-id="element-3280">
                  <div className="col-span-1 text-center text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-3281">
                    {index + 1}
                  </div>
                  <div className="col-span-2 pr-2" data-id="element-3282">
                    <input type="number" className="w-full border border-gray-300 dark:border-gray-600 rounded-md p-1.5 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white" placeholder="0" min={0} value={slab.minTxnCount || ''} onChange={e => updateSlab(slab.id, 'minTxnCount', Number(e.target.value))} data-id="element-3283" />
                  </div>
                  <div className="col-span-2 pr-2" data-id="element-3284">
                    <input type="number" className="w-full border border-gray-300 dark:border-gray-600 rounded-md p-1.5 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white" placeholder="0" min={0} value={slab.maxTxnCount || ''} onChange={e => updateSlab(slab.id, 'maxTxnCount', Number(e.target.value))} data-id="element-3285" />
                  </div>
                  <div className="col-span-3 pr-2" data-id="element-3286">
                    <select className="w-full border border-gray-300 dark:border-gray-600 rounded-md p-1.5 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white" value={slab.slabRewardType} onChange={e => updateSlab(slab.id, 'slabRewardType', e.target.value)} data-id="element-3287">
                      <option value="amount" data-id="element-3288">Fixed Amount</option>
                      <option value="percentage" data-id="element-3289">Percentage</option>
                    </select>
                  </div>
                  <div className="col-span-3 pr-2" data-id="element-3290">
                    <div className="relative" data-id="element-3291">
                      <input type="number" className="w-full border border-gray-300 dark:border-gray-600 rounded-md p-1.5 text-sm pr-10 bg-white dark:bg-gray-800 text-gray-900 dark:text-white" placeholder="0" min={0} max={slab.slabRewardType === 'percentage' ? 10 : undefined} step={slab.slabRewardType === 'percentage' ? 0.1 : 1} value={slab.slabRewardValue || ''} onChange={e => {
                  let val = Number(e.target.value);
                  if (slab.slabRewardType === 'percentage' && val > 10) val = 10;
                  updateSlab(slab.id, 'slabRewardValue', val);
                }} data-id="element-3292" />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs" data-id="element-3293">
                        {slab.slabRewardType === 'percentage' ? '%' : 'PKR'}
                      </span>
                    </div>
                  </div>
                  <div className="col-span-1 text-center" data-id="element-3294">
                    <button onClick={() => removeSlab(slab.id)} disabled={slabs.length <= 1} className="p-1 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded disabled:opacity-30 disabled:cursor-not-allowed transition-colors" title="Remove slab" data-id="element-3295">
                      <TrashIcon className="h-4 w-4" data-id="element-3296" />
                    </button>
                  </div>
                </div>)}
            </div>

            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2" data-id="element-3297">
              Define transaction count ranges and corresponding rewards. Slab
              ranges must not overlap. Percentage values are capped at 10%.
            </p>
          </div>}

        {/* Max Reward Caps — shown for all reward types */}
        {formData.rewardType !== 'tiered' && <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6" data-id="element-3298">
            <div data-id="element-3299">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-3300">
                Max Reward per Cycle
              </label>
              <div className="relative" data-id="element-3301">
                <input type="text" className="w-full border border-gray-300 dark:border-gray-600 rounded-md p-2 pr-12 bg-white dark:bg-gray-800 text-gray-900 dark:text-white" placeholder="0" value={getAmountDisplay('maxRewardPerCycle', formData.maxRewardPerCycle)} onChange={e => handleAmountChange('maxRewardPerCycle', e.target.value)} data-id="element-3302" />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" data-id="element-3303">
                  PKR
                </span>
              </div>
            </div>
            <div data-id="element-3304">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-3305">
                Max Reward per Campaign
              </label>
              <div className="relative" data-id="element-3306">
                <input type="text" className="w-full border border-gray-300 dark:border-gray-600 rounded-md p-2 pr-12 bg-white dark:bg-gray-800 text-gray-900 dark:text-white" placeholder="0" value={getAmountDisplay('maxRewardPerCampaign', formData.maxRewardPerCampaign)} onChange={e => handleAmountChange('maxRewardPerCampaign', e.target.value)} data-id="element-3307" />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" data-id="element-3308">
                  PKR
                </span>
              </div>
            </div>
          </div>}

        {formData.rewardType === 'tiered' && <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6" data-id="element-3309">
            <div data-id="element-3310">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-3311">
                Max Reward per Merchant per Cycle
              </label>
              <div className="relative" data-id="element-3312">
                <input type="text" className="w-full border border-gray-300 dark:border-gray-600 rounded-md p-2 pr-12 bg-white dark:bg-gray-800 text-gray-900 dark:text-white" placeholder="0" value={getAmountDisplay('maxRewardPerCycle', formData.maxRewardPerCycle)} onChange={e => handleAmountChange('maxRewardPerCycle', e.target.value)} data-id="element-3313" />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" data-id="element-3314">
                  PKR
                </span>
              </div>
            </div>
            <div data-id="element-3315">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-3316">
                Max Reward per Campaign
              </label>
              <div className="relative" data-id="element-3317">
                <input type="text" className="w-full border border-gray-300 dark:border-gray-600 rounded-md p-2 pr-12 bg-white dark:bg-gray-800 text-gray-900 dark:text-white" placeholder="0" value={getAmountDisplay('maxRewardPerCampaign', formData.maxRewardPerCampaign)} onChange={e => handleAmountChange('maxRewardPerCampaign', e.target.value)} data-id="element-3318" />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" data-id="element-3319">
                  PKR
                </span>
              </div>
            </div>
          </div>}
      </div>
    </div>;
  const renderStep4 = () => <div className="space-y-6" data-id="element-3320">
      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700" data-id="element-3321">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4" data-id="element-3322">
          Budget Management & Controls
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-3323">
          <div data-id="element-3324">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-3325">
              Total Campaign Budget (PKR)
            </label>
            <div className="relative" data-id="element-3326">
              <input type="text" className="w-full border border-gray-300 dark:border-gray-600 rounded-md p-2 pr-12 bg-white dark:bg-gray-800 text-gray-900 dark:text-white" placeholder="0" value={getAmountDisplay('totalBudget', formData.totalBudget)} onChange={e => handleAmountChange('totalBudget', e.target.value)} data-id="element-3327" />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" data-id="element-3328">
                PKR
              </span>
            </div>
          </div>
          <div data-id="element-3329">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-3330">
              Budget Per Cycle (PKR)
            </label>
            <div className="relative" data-id="element-3331">
              <input type="text" className="w-full border border-gray-300 dark:border-gray-600 rounded-md p-2 pr-12 bg-white dark:bg-gray-800 text-gray-900 dark:text-white" placeholder="0" value={getAmountDisplay('cycleBudget', formData.cycleBudget)} onChange={e => handleAmountChange('cycleBudget', e.target.value)} data-id="element-3332" />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" data-id="element-3333">
                PKR
              </span>
            </div>
          </div>
          <div className="md:col-span-2" data-id="element-3334">
            <label className="flex items-center space-x-2 text-gray-700 dark:text-gray-300" data-id="element-3335">
              <input type="checkbox" checked={formData.autoStop} onChange={e => setFormData({
              ...formData,
              autoStop: e.target.checked
            })} className="rounded border-gray-300 text-blue-600" data-id="element-3336" />
              <span data-id="element-3337">Auto Stop on 100% Budget Utilization</span>
            </label>
          </div>
        </div>

        <div className="mt-6 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md" data-id="element-3338">
          <h4 className="text-sm font-medium text-yellow-800 dark:text-yellow-300 mb-2" data-id="element-3339">
            Fraud & Control Safeguards
          </h4>
          <ul className="list-disc list-inside text-sm text-yellow-700 dark:text-yellow-400 space-y-1" data-id="element-3340">
            <li data-id="element-3341">Minimum transaction threshold enabled</li>
            <li data-id="element-3342">Velocity check integration active</li>
            <li data-id="element-3343">Duplicate artificial transaction detection enabled</li>
            <li data-id="element-3344">Reversal revalidation before payout enabled</li>
          </ul>
        </div>
      </div>
    </div>;
  const renderStep5 = () => <div className="space-y-6" data-id="element-3345">
      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700" data-id="element-3346">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4" data-id="element-3347">
          Review Configuration
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-y-4 gap-x-8 text-sm" data-id="element-3348">
          <div data-id="element-3349">
            <span className="text-gray-500 dark:text-gray-400 block" data-id="element-3350">
              Campaign Name
            </span>
            <span className="font-medium text-gray-900 dark:text-white" data-id="element-3351">
              {formData.name || '—'}
            </span>
          </div>
          <div data-id="element-3352">
            <span className="text-gray-500 dark:text-gray-400 block" data-id="element-3353">
              Frequency
            </span>
            <span className="font-medium text-gray-900 dark:text-white capitalize" data-id="element-3354">
              {formData.frequency}
            </span>
          </div>
          <div data-id="element-3355">
            <span className="text-gray-500 dark:text-gray-400 block" data-id="element-3356">
              Target Logic
            </span>
            <span className="font-medium text-gray-900 dark:text-white capitalize" data-id="element-3357">
              {formData.targetLogic.replace(/_/g, ' ')}
            </span>
          </div>
          <div data-id="element-3358">
            <span className="text-gray-500 dark:text-gray-400 block" data-id="element-3359">
              Reward Type
            </span>
            <span className="font-medium text-gray-900 dark:text-white capitalize" data-id="element-3360">
              {formData.rewardType}
            </span>
          </div>
          {formData.rewardType === 'tiered' ? <div className="md:col-span-2" data-id="element-3361">
              <span className="text-gray-500 dark:text-gray-400 block mb-2" data-id="element-3362">
                Reward Slabs
              </span>
              <div className="border border-gray-200 dark:border-gray-700 rounded-md overflow-hidden" data-id="element-3363">
                <div className="grid grid-cols-4 bg-gray-50 dark:bg-gray-700 px-3 py-2 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase" data-id="element-3364">
                  <div data-id="element-3365">Min Txn</div>
                  <div data-id="element-3366">Max Txn</div>
                  <div data-id="element-3367">Type</div>
                  <div data-id="element-3368">Value</div>
                </div>
                {slabs.map(slab => <div key={slab.id} className="grid grid-cols-4 px-3 py-2 border-t border-gray-200 dark:border-gray-700 text-sm text-gray-900 dark:text-white" data-id="element-3369">
                    <div data-id="element-3370">{slab.minTxnCount}</div>
                    <div data-id="element-3371">{slab.maxTxnCount}</div>
                    <div className="capitalize" data-id="element-3372">{slab.slabRewardType}</div>
                    <div data-id="element-3373">
                      {slab.slabRewardValue}
                      {slab.slabRewardType === 'percentage' ? '%' : ' PKR'}
                    </div>
                  </div>)}
              </div>
            </div> : <div data-id="element-3374">
              <span className="text-gray-500 dark:text-gray-400 block" data-id="element-3375">
                Reward Value
              </span>
              <span className="font-medium text-gray-900 dark:text-white" data-id="element-3376">
                {formData.rewardType === 'percentage' ? `${formData.rewardValue}%` : `PKR ${formData.rewardValue.toLocaleString()}`}
              </span>
            </div>}
          <div data-id="element-3377">
            <span className="text-gray-500 dark:text-gray-400 block" data-id="element-3378">
              Total Budget
            </span>
            <span className="font-medium text-gray-900 dark:text-white" data-id="element-3379">
              PKR {formData.totalBudget.toLocaleString()}
            </span>
          </div>
          <div data-id="element-3380">
            <span className="text-gray-500 dark:text-gray-400 block" data-id="element-3381">
              Stacking
            </span>
            <span className="font-medium text-gray-900 dark:text-white" data-id="element-3382">
              {formData.stacking ? 'Allowed' : 'Not Allowed'}
            </span>
          </div>
        </div>

        <div className="mt-6 flex items-start space-x-3 p-4 border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20 rounded-md" data-id="element-3383">
          <input type="checkbox" className="mt-1 rounded border-gray-300 text-blue-600" data-id="element-3384" />
          <p className="text-sm text-blue-800 dark:text-blue-300" data-id="element-3385">
            I confirm that this cashback campaign complies with internal pricing
            policies and budget approvals. Submitting this will trigger the
            Maker-Checker workflow.
          </p>
        </div>
      </div>
    </div>;
  return <div className="w-full" data-id="element-3386">
      <div className="mb-8" data-id="element-3387">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-3388">
          Create Cashback Campaign
        </h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400" data-id="element-3389">
          Configure rules, rewards, and budget for merchant incentives
        </p>
      </div>

      {/* Progress Steps */}
      <div className="mb-8" data-id="element-3390">
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-2 shadow-sm" data-id="element-3391">
          <div className="flex items-center justify-between px-2" data-id="element-3392">
            {STEPS.map((step, index) => {
            const Icon = step.icon;
            return <Fragment key={step.id} data-id="element-3393">
                  <div className={`flex flex-col items-center ${currentStep === step.id ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400 dark:text-gray-500'}`} data-id="element-3394">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center mb-1 ${currentStep === step.id ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400' : currentStep > step.id ? 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400' : 'bg-gray-100 dark:bg-gray-700'}`} data-id="element-3395">
                      {currentStep > step.id ? <CheckIcon className="h-6 w-6" data-id="element-3396" /> : <Icon className="h-5 w-5" data-id="element-3397" />}
                    </div>
                    <span className="text-xs font-medium" data-id="element-3398">{step.name}</span>
                  </div>
                  {index < STEPS.length - 1 && <div className={`flex-1 h-0.5 mx-4 ${currentStep > step.id ? 'bg-green-500' : 'bg-gray-200 dark:bg-gray-700'}`} data-id="element-3399" />}
                </Fragment>;
          })}
          </div>
        </div>
      </div>

      <Card data-id="element-3400">
        <div className="min-h-[400px]" data-id="element-3401">
          {currentStep === 1 && renderStep1()}
          {currentStep === 2 && renderStep2()}
          {currentStep === 3 && renderStep3()}
          {currentStep === 4 && renderStep4()}
          {currentStep === 5 && renderStep5()}
        </div>

        <div className="flex justify-between mt-8 pt-4 border-t border-gray-200 dark:border-gray-700" data-id="element-3402">
          <button onClick={handleBack} disabled={currentStep === 1} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 flex items-center" data-id="element-3403">
            <ChevronLeftIcon className="h-4 w-4 mr-1" data-id="element-3404" /> Back
          </button>

          {currentStep < STEPS.length ? <button onClick={handleNext} className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 flex items-center" data-id="element-3405">
              Next <ChevronRightIcon className="h-4 w-4 ml-1" data-id="element-3406" />
            </button> : <button onClick={handleSubmit} className="px-4 py-2 bg-green-600 text-white rounded-md text-sm font-medium hover:bg-green-700 flex items-center" data-id="element-3407">
              <SaveIcon className="h-4 w-4 mr-1.5" data-id="element-3408" /> Submit for Approval
            </button>}
        </div>
      </Card>
    </div>;
};
export default CashbackCampaignForm;